import React, { useState } from "react";
import PropTypes from "prop-types";
import { Popover, Spin } from "../../../common/UIComponents";
import I18n from "../../../common/I18n";
import { TenderIcon } from "../../../common/CustomIcon";
import { fetchOrderTenderSummary } from "../../../api/agentTenders";
import { OrgContext } from "../../../context/OrgContext";

// Orders-listing "Tendered to Agent" indicator. Rendered per grid row, so it
// stays as a bare icon until the FIRST hover — only then is the Popover
// mounted AND the leg summary fetched (the listing index itself only carries
// the is_agent_tendered boolean; no tender documents are hydrated per page).
const AgentTenderIndicator = ({ order }) => {
  const { currentOrg } = React.useContext(OrgContext);
  const [active, setActive] = useState(false);
  const [legs, setLegs] = useState(null); // null = loading

  const activate = () => {
    setActive(true);
    fetchOrderTenderSummary(currentOrg.id, order.id).then((result) => {
      setLegs(result.success ? result.legs || [] : []);
    });
  };

  // purple = at least one leg TENDERED; amber = draft-only (SAVED legs park
  // the order out of preplan/dispatch, so the listing must flag it)
  const drafted = order.agent_tender_state === "SAVED";
  const icon = (
    <span
      style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
      onMouseEnter={active ? undefined : activate}
      data-testid={`tender-indicator-${order.customer_order_number}`}
      data-tender-state={order.agent_tender_state || "TENDERED"}
    >
      <TenderIcon color={drafted ? "#c98a06" : "#6b3fc9"} width="15" height="14" />
    </span>
  );

  if (!active) return icon;

  const LEG_LETTER = { PICKUP: "P", LINEHAUL: "L", DELIVERY: "D" };
  const LEG_TONE = { PICKUP: "atb-leg--pickup", LINEHAUL: "atb-leg--linehaul", DELIVERY: "atb-leg--delivery" };

  return (
    <Popover
      placement="right"
      title={drafted ? I18n.t("agent_tenders.popover.title_drafted") : I18n.t("agent_tenders.popover.title")}
      // popover portals to <body>, outside the .lp-v3 root — re-scope it
      overlayClassName="lp-v3 ati-popover"
      // mounted mid-hover, so open immediately; hover trigger takes over after
      defaultVisible
      content={
        <div className="ati-body">
          {legs === null ? (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <Spin size="small" />
            </div>
          ) : (
            <div className="ati-grid">
              {legs.map((leg, index) => (
                <React.Fragment key={index}>
                  {/* TND links straight to that tender; bottom link shows the whole order */}
                  {leg.tender_number ? (
                    <a
                      className="ati-tnd"
                      href={`/agent_tender_board?search=${leg.tender_number}`}
                      data-turbolinks="false"
                    >
                      {leg.tender_number}
                    </a>
                  ) : (
                    <span className="ati-tnd ati-tnd--draft">{I18n.t("agent_tenders.status.draft")}</span>
                  )}
                  <span className={`atb-leg-badge ${LEG_TONE[leg.leg_type] || ""}`}>
                    {LEG_LETTER[leg.leg_type] || leg.leg_type}
                  </span>
                  <span className="ati-agent">{leg.agent_name}</span>
                  <span
                    className={`ati-status ati-status--${(leg.status === "SAVED"
                      ? "saved"
                      : leg.operation_status || "tendered"
                    ).toLowerCase()}`}
                  >
                    {leg.status === "SAVED"
                      ? I18n.t("agent_tenders.status.drafted")
                      : leg.operation_status
                      ? leg.operation_status.replace("_", " ")
                      : I18n.t("agent_tenders.popover.tendered")}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          {/* data-turbolinks=false: Turbolinks body-swaps SPA routes into a blank
              page — force a real full-page navigation instead. */}
          <a
            href={`/agent_tender_board?search=${order.customer_order_number}`}
            data-turbolinks="false"
            className="ati-link"
          >
            {I18n.t("agent_tenders.popover.open_board")} →
          </a>
        </div>
      }
    >
      {icon}
    </Popover>
  );
};

AgentTenderIndicator.propTypes = {
  order: PropTypes.shape().isRequired,
};

// Re-render only when the row's tender flag actually changes.
export default React.memo(
  AgentTenderIndicator,
  (prev, next) =>
    prev.order.id === next.order.id &&
    prev.order.is_agent_tendered === next.order.is_agent_tendered &&
    prev.order.agent_tender_state === next.order.agent_tender_state
);
